import type { StatusToken } from "@lib/config/statusTokens";
import { cn } from "@lib/utils/cn";
import { RotateCw, Search, X } from "lucide-react";
import {
  CATEGORIE_TICKET,
  STATUT_TICKET,
  type StatusConfig,
  toTicketStatusOptions,
} from "../../config/statuses";
import type { TicketsFilters } from "../../hooks/useTicketsFilters";
import { DevViewToggle } from "../DevViewToggle";

const categoryOptions = Object.values(CATEGORIE_TICKET).map(({ code, label }) => ({
  value: code,
  label,
}));

/** Resolve the L2.5 token for a code, falling back to the neutral draft token. */
function tokenOf(config: Readonly<Record<string, StatusConfig>>, code: string): StatusToken {
  return config[code]?.token ?? "draft";
}

/** Small colored dot reflecting the selected value's semantic token. */
function SelectDot({ color }: { color: string }) {
  return (
    <span
      className="pointer-events-none absolute left-2.5 top-1/2 h-2 w-2 -translate-y-1/2 rounded-full"
      style={{ backgroundColor: color }}
    />
  );
}

const SELECT_CLASSES =
  "cursor-pointer appearance-none rounded-[var(--radius-field)] border border-[var(--line)] bg-[var(--surface-sunken)] px-2.5 py-2 pr-8 text-xs text-[var(--ink)] shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] transition-[border-color,box-shadow] duration-[var(--duration-fast)] ease-[var(--ease-out)] focus:border-[var(--brand)] focus:shadow-[0_0_0_3px_var(--brand-soft)] focus:outline-none";

interface TicketsToolbarProps {
  filters: TicketsFilters;
  onRefresh: () => void;
  isRefreshing: boolean;
}

export function TicketsToolbar({ filters, onRefresh, isRefreshing }: TicketsToolbarProps) {
  return (
    <div className="flex flex-wrap items-center gap-2.5">
      {/* Search */}
      <div className="flex min-w-[200px] max-w-[320px] flex-1 items-center gap-2 rounded-[var(--radius-field)] border border-[var(--line)] bg-[var(--surface-sunken)] px-2.5 py-2 shadow-[inset_0_1px_2px_rgba(0,0,0,0.05)] transition-[border-color,box-shadow] duration-[var(--duration-base)] ease-[var(--ease-out)] focus-within:border-[var(--brand)] focus-within:shadow-[inset_0_1px_2px_rgba(0,0,0,0.05),0_0_0_3px_var(--brand-soft)]">
        <Search className="h-3.5 w-3.5 shrink-0 text-[var(--ink-faint)]" />
        <input
          type="text"
          className="min-w-0 flex-1 bg-transparent text-[12.5px] text-[var(--ink)] outline-none placeholder:text-[var(--ink-faint)]"
          placeholder="Rechercher un ticket..."
          value={filters.searchQuery}
          onChange={(e) => filters.setSearchQuery(e.target.value)}
        />
        {filters.searchQuery && (
          <button
            type="button"
            className="flex text-[var(--ink-faint)] transition-colors duration-[var(--duration-fast)] hover:text-[var(--ink)]"
            onClick={() => filters.setSearchQuery("")}
            aria-label="Effacer la recherche"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Status select */}
      <div className="relative">
        {filters.selectedStatus && (
          <SelectDot color={`var(--status-${tokenOf(STATUT_TICKET, filters.selectedStatus)})`} />
        )}
        <select
          className={`${SELECT_CLASSES} ${filters.selectedStatus ? "pl-7" : ""}`}
          value={filters.selectedStatus}
          onChange={(e) => filters.setSelectedStatus(e.target.value)}
          aria-label="Filtrer par statut"
        >
          <option value="">Statut : Tous</option>
          {toTicketStatusOptions(STATUT_TICKET).map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      {/* Category select */}
      <div className="relative">
        {filters.selectedCategory && (
          <SelectDot
            color={`var(--status-${tokenOf(CATEGORIE_TICKET, filters.selectedCategory)})`}
          />
        )}
        <select
          className={`${SELECT_CLASSES} ${filters.selectedCategory ? "pl-7" : ""}`}
          value={filters.selectedCategory}
          onChange={(e) => filters.setSelectedCategory(e.target.value)}
          aria-label="Filtrer par catégorie"
        >
          <option value="">Catégorie : Toutes</option>
          {categoryOptions.map((opt) => (
            <option key={opt.value} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      </div>

      <DevViewToggle isDevView={filters.isDevView} onToggle={filters.setIsDevView} />

      {/* Refresh */}
      <button
        type="button"
        className="btn btn-ghost btn-sm rounded-md"
        onClick={onRefresh}
        disabled={isRefreshing}
        aria-label="Rafraîchir la liste"
      >
        <RotateCw className={cn("w-4 h-4", isRefreshing && "animate-spin")} />
      </button>
    </div>
  );
}
